import { buildWorkbookParts, worksheetFrame, xlsxRowXml, type Row, type Sheet } from "./xlsx";

export type ChunkedSheet = Omit<Sheet, "rows"> & {
  rows: Blob;
  rowCount: number;
  columnCount: number;
};

export class XlsxSizeLimitError extends Error {
  constructor() {
    super("The export exceeds the download size limit. Choose a narrower date range.");
    this.name = "XlsxSizeLimitError";
  }
}

const MAX_ROWS = 1_048_576;
const MAX_COLUMNS = 16_384;
// The all-one values are ZIP64 sentinels and cannot describe classic ZIP data.
const ZIP32_LIMIT = 0xffffffff;
const ZIP16_LIMIT = 0xffff;
const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const CRC_YIELD_BYTES = 1024 * 1024;

function checkCount(value: number, maximum: number, kind: string): void {
  if (!Number.isInteger(value) || value < 0) {
    throw new Error(`The worksheet ${kind} count must be a non-negative integer.`);
  }
  if (value > maximum) {
    throw new Error(`The export exceeds Excel's ${kind} limit (${maximum.toLocaleString("en-US")}).`);
  }
}

/** Serialize one bounded page, using final worksheet row numbers. */
export function xlsxRowChunk(rows: Row[], firstRowNumber: number): Blob {
  if (!Number.isInteger(firstRowNumber) || firstRowNumber < 1) {
    throw new Error("The first worksheet row must be a positive integer.");
  }
  checkCount(firstRowNumber, MAX_ROWS, "row");
  checkCount(firstRowNumber + rows.length - 1, MAX_ROWS, "row");
  return new Blob(rows.map((row, index) => {
    checkCount(row.cells.length, MAX_COLUMNS, "column");
    return xlsxRowXml(row, firstRowNumber + index);
  }));
}

function checkAbort(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw new DOMException("The download was cancelled.", "AbortError");
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let index = 0; index < table.length; index++) {
    let crc = index;
    for (let bit = 0; bit < 8; bit++) crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
    table[index] = crc >>> 0;
  }
  return table;
})();

async function blobCrc32(blob: Blob, signal: AbortSignal | undefined): Promise<number> {
  const reader = blob.stream().getReader();
  let crc = 0xffffffff;
  let bytesSinceYield = 0;
  try {
    while (true) {
      checkAbort(signal);
      const chunk = await reader.read();
      checkAbort(signal);
      if (chunk.done) break;
      // A stream may return arbitrarily large chunks. Yield inside the byte
      // loop too, so the Cancel button remains usable for every Blob source.
      for (let start = 0; start < chunk.value.length; start += 65_536) {
        const end = Math.min(start + 65_536, chunk.value.length);
        for (let index = start; index < end; index++) {
          crc = (CRC_TABLE[(crc ^ (chunk.value[index] ?? 0)) & 0xff] ?? 0) ^ (crc >>> 8);
        }
        bytesSinceYield += end - start;
        if (bytesSinceYield >= CRC_YIELD_BYTES) {
          await new Promise<void>((resolve) => setTimeout(resolve, 0));
          bytesSinceYield = 0;
          checkAbort(signal);
        }
      }
    }
  } finally {
    if (signal?.aborted) await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
  return (crc ^ 0xffffffff) >>> 0;
}

type Entry = { name: Uint8Array; data: Blob };

function zipHeaders(entry: Entry, crc: number, offset: number): { local: Uint8Array; central: Uint8Array } {
  const local = new Uint8Array(30 + entry.name.length);
  const lv = new DataView(local.buffer);
  lv.setUint32(0, 0x04034b50, true);
  lv.setUint16(4, 20, true);
  lv.setUint16(12, 0x0021, true); // Fixed 1980-01-01 timestamp; method and flags stay zero.
  lv.setUint32(14, crc, true);
  lv.setUint32(18, entry.data.size, true);
  lv.setUint32(22, entry.data.size, true);
  lv.setUint16(26, entry.name.length, true);
  local.set(entry.name, 30);

  const central = new Uint8Array(46 + entry.name.length);
  const cv = new DataView(central.buffer);
  cv.setUint32(0, 0x02014b50, true);
  cv.setUint16(4, 20, true);
  cv.setUint16(6, 20, true);
  cv.setUint16(14, 0x0021, true);
  cv.setUint32(16, crc, true);
  cv.setUint32(20, entry.data.size, true);
  cv.setUint32(24, entry.data.size, true);
  cv.setUint16(28, entry.name.length, true);
  cv.setUint32(42, offset, true);
  central.set(entry.name, 46);
  return { local, central };
}

function checkZip32(size: number): void {
  if (!Number.isSafeInteger(size) || size < 0 || size >= ZIP32_LIMIT) {
    throw new Error("The export exceeds the classic ZIP file size limit. Choose a narrower date range.");
  }
}

/**
 * Assemble a STORED ZIP without copying all row data into one JS buffer.
 * Rows stay in immutable Blobs; only stream chunks and small ZIP headers are
 * read into JS memory. Size checks include every XML part and ZIP header.
 */
export async function buildChunkedXlsx(
  sheets: ChunkedSheet[],
  options: { signal?: AbortSignal; maxBytes?: number },
): Promise<Blob> {
  checkAbort(options.signal);
  if (sheets.length === 0) throw new Error("a workbook needs at least one sheet");
  if (sheets.length + 5 >= ZIP16_LIMIT) throw new Error("The workbook exceeds the classic ZIP entry limit.");
  const maxBytes = options.maxBytes ?? ZIP32_LIMIT - 1;
  if (!Number.isSafeInteger(maxBytes) || maxBytes < 1) throw new Error("The export size limit must be a positive whole number of bytes.");
  for (const sheet of sheets) {
    checkCount(sheet.rowCount, MAX_ROWS, "row");
    checkCount(sheet.columnCount, MAX_COLUMNS, "column");
    checkCount(sheet.columns?.length ?? 0, MAX_COLUMNS, "column");
    checkZip32(sheet.rows.size);
  }
  const encoder = new TextEncoder();
  // Empty rows create only small metadata. Worksheet content is supplied below.
  const metadata = buildWorkbookParts(sheets.map((sheet) => ({ ...sheet, rows: [] })))
    .filter((part) => !part.name.startsWith("xl/worksheets/"));
  const entries: Entry[] = metadata.map((part) => ({ name: encoder.encode(part.name), data: new Blob([part.xml]) }));
  sheets.forEach((sheet, index) => {
    const { prefix, suffix } = worksheetFrame(sheet, sheet.rowCount, sheet.columnCount);
    entries.push({ name: encoder.encode(`xl/worksheets/sheet${index + 1}.xml`), data: new Blob([prefix, sheet.rows, suffix]) });
  });
  let localSize = 0;
  let centralSize = 0;
  for (const entry of entries) {
    checkZip32(entry.data.size);
    if (entry.name.length >= ZIP16_LIMIT) throw new Error("The workbook exceeds the classic ZIP filename limit.");
    localSize += 30 + entry.name.length + entry.data.size;
    centralSize += 46 + entry.name.length;
  }
  const finalSize = localSize + centralSize + 22;
  checkZip32(finalSize);
  if (finalSize > maxBytes) throw new XlsxSizeLimitError();

  const parts: BlobPart[] = [];
  const directory: BlobPart[] = [];
  let offset = 0;
  for (const entry of entries) {
    checkAbort(options.signal);
    const crc = await blobCrc32(entry.data, options.signal);
    const { local, central } = zipHeaders(entry, crc, offset);
    parts.push(local.buffer as ArrayBuffer, entry.data);
    directory.push(central.buffer as ArrayBuffer);
    offset += local.length + entry.data.size;
  }
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, entries.length, true);
  ev.setUint16(10, entries.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, localSize, true);
  checkAbort(options.signal);
  return new Blob([...parts, ...directory, end.buffer], { type: XLSX_MIME });
}
